/**
 * Blocks Service
 *
 * Pure business logic for time block calculations and transformations.
 * No React or Firebase dependencies.
 */

import { Block } from '@/types';

// ============================================
// Sorting Functions
// ============================================

/**
 * Sort blocks by their start time in ascending order.
 *
 * @param blocks - Array of blocks with startTime property
 * @returns New sorted array (does not mutate original)
 */
export function sortBlocksByStartTime<T extends { startTime: number }>(blocks: T[]): T[] {
  return [...blocks].sort((a, b) => a.startTime - b.startTime);
}

// ============================================
// Progress Calculations
// ============================================

/**
 * Calculate block progress statistics.
 *
 * @param blocks - Array of blocks to analyze
 * @returns Object with completedCount, missedCount, and totalCount (excludes cancelled blocks)
 */
export function getBlockProgress(blocks: Block[]): {
  completedCount: number;
  missedCount: number;
  totalCount: number;
  missedMinutes: number;
} {
  let completedCount = 0;
  let missedCount = 0;
  let missedMinutes = 0;
  let totalCount = 0;

  for (const block of blocks) {
    if (block.status === 'cancelled') continue;

    totalCount++;
    if (block.status === 'completed') {
      completedCount++;
    } else if (block.status === 'missed') {
      missedCount++;
      missedMinutes += block.duration;
    }
  }

  return { completedCount, missedCount, totalCount, missedMinutes };
}

// ============================================
// Date Formatting
// ============================================

/**
 * Format a Date object as a YYYY-MM-DD string.
 *
 * @param date - The date to format
 * @returns Date string in YYYY-MM-DD format
 */
export function formatDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get all dates for the current week (Monday through Sunday).
 *
 * @param referenceDate - Optional reference date (defaults to now)
 * @returns Array of 7 Date objects representing the week
 */
export function getWeekDates(referenceDate: Date = new Date()): Date[] {
  const day = referenceDate.getDay();
  const diff = referenceDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start

  const monday = new Date(referenceDate);
  monday.setDate(diff);
  monday.setHours(0, 0, 0, 0);

  const weekDates: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    weekDates.push(date);
  }

  return weekDates;
}

// ============================================
// Time Conversion
// ============================================

/**
 * Convert minutes from midnight to a time string.
 *
 * @param minutes - Minutes from midnight (e.g., 540 = 9:00 AM)
 * @returns Time string in "HH:MM AM/PM" format
 */
export function minutesToTimeString(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
}

/**
 * Get the end time of a block in minutes from midnight.
 *
 * @param block - The block to calculate end time for
 * @returns End time in minutes from midnight
 */
export function getBlockEndTime(block: Block): number {
  return block.startTime + block.duration;
}

// ============================================
// Block Filtering
// ============================================

/**
 * Filter blocks by their status.
 *
 * @param blocks - Array of blocks to filter
 * @param status - Status to filter by, or 'all' to return all blocks
 * @returns Filtered array of blocks
 */
export function filterBlocksByStatus(blocks: Block[], status: Block['status'] | 'all'): Block[] {
  if (status === 'all') {
    return blocks;
  }
  return blocks.filter((block) => block.status === status);
}

/**
 * Get active (non-cancelled) blocks.
 *
 * @param blocks - Array of blocks to filter
 * @returns Array of blocks that are not cancelled
 */
export function getActiveBlocks(blocks: Block[]): Block[] {
  return blocks.filter((block) => block.status !== 'cancelled');
}

/**
 * Calculate total duration of blocks in minutes.
 *
 * @param blocks - Array of blocks to sum
 * @returns Total duration in minutes
 */
export function getTotalDuration(blocks: Block[]): number {
  return blocks.reduce((sum, block) => sum + block.duration, 0);
}
